import { format } from "date-fns";
import Konva from "konva";
import i18n from "i18next";
import { hasPlatform } from "../../lib/stream-platform";
import { artFontStyle } from "../../lib/schedule-art/art-fonts";
import { formatArtValue, type ArtValue } from "../../lib/schedule-art/format-art-value";
import { platformImage } from "../../lib/schedule-art/platform-images";
import { layoutCanvasPoint } from "../../lib/schedule-art/layout-coordinates";
import type { ArtDate, ArtElement, ArtTextStyle } from "../../lib/schedule-presets/art-metadata";
import { weekdayKeys } from "../../lib/schedule-time/schedule-dates";
import type { MetadataDay } from "../../lib/metadata-history/metadata-targets";
import type { useScheduleStore } from "../../stores/schedule-store";
import { attachCanvasDrag, attachMetadataDrag, type moveMetadataElement } from "./metadata-drag";
import type { BuilderStep } from "../../stores/metadata-builder-store";
import { builderTextVisible } from "../../lib/metadata-builder/preview-visibility";

const alignAnchor = { left: 0, center: 0.5, right: 1 };

export type ArtData =Pick<ReturnType<typeof useScheduleStore.getState>, "preset" | "week" | "slots" | "commonOptions" | "profile" | "canvasMetadata" | "artistHandlePosition" | "artistHandleScale" | "artistHandleRotation" | "offlineMessages" | "offlineActive"> & { dayDatesVisible?: boolean; previewDays?: MetadataDay[] | null; builderStep?: BuilderStep };
export function renderArtText(
  group: Konva.Group,
  data: ArtData,
  offlineLabel: string,
  onMoveMetadata?: typeof moveMetadataElement,
  onMoveArtistHandle?: (point: number[]) => void,
) {
  group.destroyChildren();
  const { preset, week, slots, profile } = data;
  const metadata = data.canvasMetadata ?? preset.metadata;
  if (!metadata) return;
  const draw = (element: ArtElement | ArtDate, origin: number[], bindings: Record<string, ArtValue>, path: string, dayKey?: string, layoutRotation = 0) => {
    const bind = "bind" in element ? element.bind : "day.date";
    if (data.builderStep && !builderTextVisible(data.builderStep, path, bind)) return;
    const value = bindings[bind];
    if (value === undefined || value === "") return;
    const isArtistHandle = bind === "profile.artist_handle";
    const metadataPoint = "position" in element ? element.position : element.offset;
    const point = isArtistHandle && !onMoveMetadata && data.artistHandlePosition
      ? data.artistHandlePosition
      : metadataPoint;
    const artistScale = isArtistHandle && !onMoveMetadata ? data.artistHandleScale : 1;
    const elementRotation = isArtistHandle && !onMoveMetadata
      ? data.artistHandleRotation ?? element.rotation ?? 0
      : element.rotation ?? 0;
    const canvasPoint = layoutCanvasPoint(point, origin, layoutRotation);
    const position = {
      x: canvasPoint[0]!,
      y: canvasPoint[1]!,
      rotation: layoutRotation + elementRotation,
      scaleX: artistScale,
      scaleY: artistScale,
    };
    const item = new Konva.Group(position);
    item.setAttrs({ metadataPath: path, metadataDay: dayKey, metadataOffset: point, metadataOrigin: origin, metadataBind: bind, metadataLayoutRotation: layoutRotation });
    group.add(item);
    if (onMoveMetadata) {
      attachMetadataDrag(item, path, origin, onMoveMetadata);
    } else if (isArtistHandle && onMoveArtistHandle) {
      attachCanvasDrag(item, origin, onMoveArtistHandle);
    }
    if ("type" in element && element.type === "icon") {
      const size = 48 * (element.scale ?? 100) / 100;
      const memberOnlyBind = bind.replace(".platform_icon", ".is_member_only");
      const memberOnly = Boolean(bindings[memberOnlyBind]);
      const platform = String(value);
      if (memberOnly && platform === "both") {
        ["YTMember", "Twitch"].forEach((platform, index) => {
          const image = platformImage(platform, element.black_icon);
          if (image) item.add(new Konva.Image({ image, x: index * size / 2, y: size / 4, width: size / 2, height: size / 2 }));
        });
        return;
      }
      const icon = memberOnly && platform === "YT" ? "YTMember" : platform;
      const image = platformImage(icon, element.black_icon);
      if (image) item.add(new Konva.Image({ image, width: size, height: size }));
      return;
    }
    const styles: Record<string, ArtTextStyle> = metadata.styles;
    const formats: Record<string, string> = metadata.formats;
    const style = styles[element.style ?? ""];
    if (!style) return;
    const outsideStroke = style.stroke?.position === "outside";
    const configuredStrokeWidth = style.stroke?.width ?? 0;
    const strokeWidth = outsideStroke
      ? configuredStrokeWidth * 2
      : configuredStrokeWidth;
    const formatted = formatArtValue(value, formats["format" in element ? element.format ?? "" : ""]);
    const text = new Konva.Text({
      text: style.capitalize ? formatted.toUpperCase() : formatted,
      ...artFontStyle(style),
      fontSize: style.size, fill: style.color, padding: 0,
      stroke: style.stroke?.color,
      strokeWidth,
      strokeEnabled: strokeWidth > 0,
      fillAfterStrokeEnabled: outsideStroke,
      lineJoin: outsideStroke ? "round" : "miter",
    });
    const align = style.align ?? "left";
    const textWidth = text.width();
    text.setAttrs({ align, width: textWidth, offsetX: textWidth * alignAnchor[align] });
    item.add(text);
  };
  const globals: Record<string, ArtValue> = {
    "week.start_date": week[0]!, "week.end_date": week[6]!,
    "week.range": [week[0]!, week[6]!],
    "profile.artist_handle": profile.artist_handle,
  };
  metadata.globals.forEach((element, index) => draw(element, [0, 0], globals, `globals.${index}`));
  week.forEach((date, index) => {
    const dayKey = weekdayKeys[index]!;
    if (data.previewDays && !data.previewDays.includes(dayKey)) return;
    const day = metadata.days[dayKey];
    if (!day) return;
    const key = format(date, "yyyy-MM-dd");
    const active = [slots[`${key}-1`], slots[`${key}-2`]].filter(slot => slot?.active);
    const bindings: Record<string, ArtValue> = { "day.date": date, "offline.label": offlineLabel };
    if (data.offlineActive[key]) bindings["offline.message"] = data.offlineMessages[key]?.trim() || offlineLabel;
    active.forEach((slot, slotIndex) => {
      const prefix = `slot${slotIndex + 1}`;
      bindings[`${prefix}.title`] = slot!.title;
      bindings[`${prefix}.subtitle`] = slot!.subheading;
      bindings[`${prefix}.time`] = slot!.time;
      const hasNoPlatform = slot!.platform === "none";
      bindings[`${prefix}.platform_icon`] = hasNoPlatform ? "" : slot!.platform;
      const memberOnly = hasPlatform(slot!.platform, "YT") && slot!.memberOnly;
      bindings[`${prefix}.is_member_only`] = memberOnly ? "true" : "";
      const memberOnlyLabel = data.commonOptions.memberOnlyLabel ?? i18n.t("schedule.memberOnly");
      bindings[`${prefix}.member_only`] = memberOnly ? memberOnlyLabel : "";
    });
    const variant = active.length === 2 ? "two_slot" : active.length === 1 ? "one_slot" : "offline";
    day[variant].forEach((element, index) => draw(element, day.origin, bindings, `days.${dayKey}.${variant}.${index}`, dayKey, day.rotation));
    if (day.date && data.dayDatesVisible !== false) draw(day.date, day.origin, bindings, `days.${dayKey}.date`, dayKey, day.rotation);
  });
}
